import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

// 添加安全的 JSON 解析函数
const safeParse = (key, defaultValue = {}) => {
  try {
    const item = localStorage.getItem(key)
    
    // 检查是否为 undefined 字符串或无效值
    if (item === 'undefined' || item === 'null' || item === null || item === undefined) {
      localStorage.removeItem(key) // 清理无效数据
      return defaultValue
    }
    
    const parsed = JSON.parse(item)
    return parsed || defaultValue
  } catch (error) {
    console.warn(`Failed to parse ${key} from localStorage:`, error)
    localStorage.removeItem(key) // 清理损坏的数据
    return defaultValue
  }
}

// 安全的获取 token
const getSafeToken = () => {
  const token = localStorage.getItem('token')
  if (token === 'undefined' || token === 'null' || token === null) {
    localStorage.removeItem('token')
    return ''
  }
  return token || ''
}

export default new Vuex.Store({
    state: {
        // 使用安全的解析方式
        userInfo: safeParse('userInfo', {}),
        baseInfo: safeParse('baseInfo', {}),
        token: getSafeToken(),
        isLoading: false
    },
    
    mutations: {
        // 设置用户信息
        setUserInfoValue(state, Value) {
            state.userInfo = Value
            // 保存时确保不是 undefined
            if (Value && typeof Value === 'object') {
                localStorage.setItem('userInfo', JSON.stringify(Value))
            } else {
                localStorage.removeItem('userInfo')
            }
        },
        
        // 设置基础信息
        setBaseInfoValue(state, Value) {
            state.baseInfo = Value
            if (Value && typeof Value === 'object') {
                localStorage.setItem('baseInfo', JSON.stringify(Value))
            } else {
                localStorage.removeItem('baseInfo')
            }
        },
        
        // 设置 token
        setToken(state, token) {
            // 验证 token 有效性
            if (token && token !== 'undefined' && token !== 'null') {
                state.token = token
                localStorage.setItem('token', token)
            } else {
                state.token = ''
                localStorage.removeItem('token')
            }
        },
        
        // 清除认证信息
        clearAuth(state) {
            state.token = ''
            state.userInfo = {}
            state.baseInfo = {}
            localStorage.removeItem('token')
            localStorage.removeItem('userInfo')
            localStorage.removeItem('baseInfo')
        },
        
        // 设置加载状态
        setLoading(state, isLoading) {
            state.isLoading = isLoading
        },
        
        // 更新用户信息的部分字段
        updateUserInfo(state, payload) {
            if (payload && typeof payload === 'object') {
                state.userInfo = { ...state.userInfo, ...payload }
                localStorage.setItem('userInfo', JSON.stringify(state.userInfo))
            }
        },
        
        // 更新基础信息的部分字段
        updateBaseInfo(state, payload) {
            if (payload && typeof payload === 'object') {
                state.baseInfo = { ...state.baseInfo, ...payload }
                localStorage.setItem('baseInfo', JSON.stringify(state.baseInfo))
            }
        }
    },
    
    getters: {
        // get方法
        getUserInfo: state => state.userInfo,
        getBaseInfo: state => state.baseInfo,
        isLoggedIn: state => !!state.token && state.token !== 'undefined',
        getToken: state => state.token,
        getLoading: state => state.isLoading,
        
        // 获取用户ID
        getUserId: state => state.userInfo.id || null,
        
        // 获取用户名
        getUsername: state => state.userInfo.username || state.userInfo.name || '',
        
        // 获取用户头像
        getUserAvatar: state => state.userInfo.avatar || state.userInfo.photo || '',
        
        // 检查用户信息是否完整
        isUserInfoComplete: state => {
            return state.userInfo && Object.keys(state.userInfo).length > 0
        }
    },
    
    actions: {
        // 登录 action
        async login({ commit }, { username, password }) {
            commit('setLoading', true)
            
            try {
                // 使用您原来的 API 调用方式
                const response = await this._vm.$http({
                    method: 'get',
                    data: { username, password },
                    url: 'member_login'
                })
                
                if (response.code === 200) {
                    commit('setToken', response.data.id)
                    commit('setUserInfoValue', response.data)
                    commit('setLoading', false)
                    return Promise.resolve(response)
                } else {
                    commit('setLoading', false)
                    return Promise.reject(new Error(response.msg))
                }
            } catch (error) {
                commit('setLoading', false)
                return Promise.reject(error)
            }
        },
        
        // 登出 action
        logout({ commit }) {
            return new Promise((resolve) => {
                // 清除认证信息
                commit('clearAuth')
                
                // 可选：清除其他相关存储
                const keysToRemove = [
                    'token', 'userInfo', 'baseInfo', 
                    'userToken', 'authToken', 'loginInfo'
                ]
                
                keysToRemove.forEach(key => {
                    localStorage.removeItem(key)
                })
                
                resolve('登出成功')
            })
        },
        
        // 初始化用户信息（应用启动时调用）
        initializeUser({ state, dispatch }) {
            return new Promise((resolve) => {
                if (state.token && state.token !== 'undefined') {
                    console.log('检测到已登录用户，token:', state.token)
                    // 可以在这里验证 token 有效性
                    dispatch('validateToken').then(valid => {
                        if (valid) {
                            console.log('token 有效，自动登录成功')
                            resolve(true)
                        } else {
                            console.log('token 无效，需要重新登录')
                            resolve(false)
                        }
                    })
                } else {
                    console.log('用户未登录')
                    resolve(false)
                }
            })
        },
        
        // 验证 token 有效性
        async validateToken({ state, commit }) {
            if (!state.token || state.token === 'undefined') {
                return false
            }
            
            try {
                // 调用验证 token 的 API
                const response = await this._vm.$http({
                    method: 'get',
                    url: 'validate_token',
                    data: { token: state.token }
                })
                
                return response.code === 200
            } catch (error) {
                console.error('token 验证失败:', error)
                // token 无效，清除认证信息
                commit('clearAuth')
                return false
            }
        },
        
        // 更新用户资料
        async updateProfile({ commit, state }, profileData) {
            commit('setLoading', true)
            
            try {
                const response = await this._vm.$http({
                    method: 'post',
                    url: 'update_profile',
                    data: { ...profileData, token: state.token }
                })
                
                if (response.code === 200) {
                    // 更新本地用户信息
                    commit('updateUserInfo', profileData)
                    commit('setLoading', false)
                    return Promise.resolve(response)
                } else {
                    commit('setLoading', false)
                    return Promise.reject(new Error(response.msg))
                }
            } catch (error) {
                commit('setLoading', false)
                return Promise.reject(error)
            }
        },
        
        // 刷新用户信息
        async refreshUserInfo({ commit, state }) {
            if (!state.token || state.token === 'undefined') {
                return Promise.reject(new Error('未登录'))
            }
            
            try {
                const response = await this._vm.$http({
                    method: 'get',
                    url: 'get_user_info',
                    data: { token: state.token }
                })
                
                if (response.code === 200) {
                    commit('setUserInfoValue', response.data)
                    return Promise.resolve(response)
                } else {
                    return Promise.reject(new Error(response.msg))
                }
            } catch (error) {
                return Promise.reject(error)
            }
        },
        
        // 清除所有数据（完全重置）
        clearAllData({ commit }) {
            return new Promise((resolve) => {
                // 清除 Vuex state
                commit('clearAuth')
                
                // 清除所有 localStorage
                localStorage.clear()
                
                // 清除 sessionStorage
                sessionStorage.clear()
                
                resolve('所有数据已清除')
            })
        },
        
        // 修复 localStorage 数据
        fixLocalStorage({ commit }) {
            return new Promise((resolve) => {
                // 清理所有可能的无效数据
                const keysToCheck = ['userInfo', 'baseInfo', 'token']
                
                keysToCheck.forEach(key => {
                    const value = localStorage.getItem(key)
                    if (value === 'undefined' || value === 'null') {
                        localStorage.removeItem(key)
                        console.log(`已清理无效的 ${key} 数据`)
                    }
                })
                
                // 重新初始化状态
                commit('setToken', getSafeToken())
                commit('setUserInfoValue', safeParse('userInfo', {}))
                commit('setBaseInfoValue', safeParse('baseInfo', {}))
                
                resolve('数据修复完成')
            })
        }
    },
    
    modules: {
        // 这里是为了给全局变量分组，所以需要写提前声明其他store文件，然后引入这里
    }
})